import { useCallback, useEffect, useRef, useState } from "react";

function readStoredWidth(storageKey, minWidth, maxWidth) {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return null;
    return Math.min(Math.max(parsed, minWidth), maxWidth);
  } catch {
    return null;
  }
}

/**
 * useResizablePanel — drag-to-resize para paneles laterales (desktop-only).
 *
 * El ancho custom se persiste en localStorage bajo `storageKey`; `width` es
 * `null` mientras el usuario no haya redimensionado (el caller conserva sus
 * clases CSS de ancho por defecto). Doble clic en el handle restaura el
 * default. El gating desktop lo aporta el handle (`PanelResizeHandle` es
 * `hidden md:block`) más el caller, que solo aplica `width` en su rama desktop.
 *
 * @param {object} opts
 * @param {string} opts.storageKey   clave de persistencia (localStorage)
 * @param {number} opts.defaultWidth fallback si el panel aún no está montado
 * @param {number} [opts.minWidth=240]
 * @param {number} [opts.maxWidth=640] además se acota a viewport - 96px al arrastrar
 * @param {"left"|"right"} [opts.handleSide="right"] borde del panel donde vive el handle
 * @param {string} [opts.cssVar] si se pasa, publica el ancho efectivo como
 *   variable CSS en <html> (p.ej. "--monitor-detail-w") para que elementos
 *   fuera del panel (controles del mapa) puedan seguirlo sin prop-drilling.
 *
 * @returns {{
 *   width: number|null,
 *   isResizing: boolean,
 *   panelRef: import("react").RefObject<HTMLElement>,
 *   handleProps: { onPointerDown: Function, onDoubleClick: Function },
 * }}
 */
export function useResizablePanel({
  storageKey,
  defaultWidth,
  minWidth = 240,
  maxWidth = 640,
  handleSide = "right",
  cssVar,
}) {
  const [width, setWidth] = useState(() =>
    typeof window === "undefined"
      ? null
      : readStoredWidth(storageKey, minWidth, maxWidth)
  );
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef(null);
  const widthRef = useRef(width);
  widthRef.current = width;

  useEffect(() => {
    if (!cssVar || typeof document === "undefined") return undefined;
    const el = document.documentElement;
    el.style.setProperty(cssVar, `${width ?? defaultWidth}px`);
    return () => el.style.removeProperty(cssVar);
  }, [cssVar, width, defaultWidth]);

  const onPointerDown = useCallback(
    (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      const startX = e.clientX;
      const startWidth =
        panelRef.current?.offsetWidth ?? widthRef.current ?? defaultWidth;
      // No dejar que el panel se coma todo el viewport: siempre quedan ≥96px
      // del contenido de atrás (mapa/tabla) visibles.
      const effectiveMax = Math.min(maxWidth, window.innerWidth - 96);
      let latest = startWidth;

      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        const raw = handleSide === "right" ? startWidth + dx : startWidth - dx;
        latest = Math.min(Math.max(raw, minWidth), effectiveMax);
        setWidth(latest);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        setIsResizing(false);
        try {
          window.localStorage.setItem(storageKey, String(Math.round(latest)));
        } catch {
          /* storage lleno/bloqueado: el ancho vive solo en memoria */
        }
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      setIsResizing(true);
    },
    [storageKey, defaultWidth, minWidth, maxWidth, handleSide]
  );

  const onDoubleClick = useCallback(() => {
    setWidth(null);
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      /* noop */
    }
  }, [storageKey]);

  return { width, isResizing, panelRef, handleProps: { onPointerDown, onDoubleClick } };
}
